// hooks/useVideoStream.ts - Updated version with JMuxer support and TLS fallback

import { useState, useEffect, useRef, useCallback } from 'react';
import JMuxer from 'jmuxer';

export interface VideoStreamStatus {
    connected: boolean;
    reconnectAttempts: number;
    error?: string;
    usingInsecure?: boolean;
}

interface UseVideoStreamOptions {
    wsUrl: string;
    videoElementId: string;
    autoReconnect?: boolean;
    maxReconnectAttempts?: number;
    reconnectDelay?: number;
    tlsConfig?: {
        enabled: boolean;
    };
    // Allow fallback to ws:// when wss:// fails (for self-signed/expired certs)
    allowInsecureFallback?: boolean;
}

export const useVideoStream = ({
                                   wsUrl,
                                   videoElementId,
                                   autoReconnect = true,
                                   maxReconnectAttempts = 5,
                                   reconnectDelay = 3000,
                                   tlsConfig,
                                   allowInsecureFallback = true,
                               }: UseVideoStreamOptions) => {
    const [status, setStatus] = useState<VideoStreamStatus>({
        connected: false,
        reconnectAttempts: 0,
    });

    const wsRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const jmuxerRef = useRef<JMuxer | null>(null);
    const reconnectAttemptsRef = useRef(0);
    // Track if we should try insecure fallback
    const triedSecureRef = useRef(false);
    const usingInsecureRef = useRef(false);

    const cleanup = useCallback(() => {
        if (wsRef.current) {
            console.log('Closing WebSocket connection');
            wsRef.current.close();
            wsRef.current = null;
        }

        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (jmuxerRef.current) {
            try {
                jmuxerRef.current.destroy();
            } catch (e) {
                console.warn('Error destroying JMuxer:', e);
            }
            jmuxerRef.current = null;
        }

        setStatus(prev => ({ ...prev, connected: false }));
    }, []);

    const startStream = useCallback(() => {
        // Determine which URL to use (secure or insecure fallback)
        let effectiveUrl = wsUrl;
        const originalIsSecure = wsUrl.toLowerCase().startsWith('wss://');

        // If we've tried secure and it failed, and fallback is allowed, use ws://
        if (originalIsSecure && triedSecureRef.current && allowInsecureFallback && !usingInsecureRef.current) {
            // Check if page is HTTP (ws:// only works from http:// pages)
            const pageIsHttp = window.location.protocol === 'http:';
            if (pageIsHttp) {
                effectiveUrl = wsUrl.replace('wss://', 'ws://');
                usingInsecureRef.current = true;
                console.warn('⚠️ Falling back to insecure WebSocket (ws://) due to TLS errors');
            } else {
                console.warn('⚠️ Cannot fallback to ws:// from HTTPS page. Please accept the certificate at:', wsUrl.replace('wss://', 'https://').split('/')[0]);
            }
        }

        console.log('Starting video stream from:', effectiveUrl);

        // Check if WSS and log TLS status
        const isSecure = effectiveUrl.toLowerCase().startsWith('wss://');
        if (isSecure) {
            console.log('🔒 Using secure WebSocket (WSS) for video stream');
        } else {
            console.warn('⚠️ Using insecure WebSocket (WS) for video stream');
        }

        cleanup();

        const videoElement = document.getElementById(videoElementId) as HTMLVideoElement;
        if (!videoElement) {
            console.error(`Video element with id "${videoElementId}" not found`);
            setStatus(prev => ({
                ...prev,
                error: 'Video element not found',
            }));
            return;
        }

        try {
            // Initialize JMuxer for H.264 video decoding
            jmuxerRef.current = new JMuxer({
                node: videoElement,
                mode: 'video',
                flushingTime: 0,
                fps: 30,
                debug: false,
            });
            console.log('JMuxer initialized');

            // Validate WebSocket URL
            const url = new URL(effectiveUrl);
            console.log('Connecting to WebSocket:', {
                protocol: url.protocol,
                host: url.host,
                pathname: url.pathname,
                isSecure,
                usingFallback: usingInsecureRef.current,
            });

            // Create WebSocket connection
            const ws = new WebSocket(effectiveUrl);
            ws.binaryType = 'arraybuffer';
            wsRef.current = ws;

            ws.onopen = () => {
                console.log('✅ Video stream WebSocket connected successfully');
                reconnectAttemptsRef.current = 0;
                triedSecureRef.current = false; // Reset on successful connection
                setStatus({
                    connected: true,
                    reconnectAttempts: 0,
                    usingInsecure: usingInsecureRef.current,
                });
            };

            ws.onmessage = (event) => {
                if (event.data instanceof ArrayBuffer && jmuxerRef.current) {
                    // Feed video data to JMuxer
                    const data = new Uint8Array(event.data);
                    jmuxerRef.current.feed({
                        video: data,
                    });
                }
            };

            ws.onerror = (error) => {
                console.error('❌ Video stream WebSocket error:', error);

                // Mark that we've tried secure connection (for fallback logic)
                if (isSecure && !usingInsecureRef.current) {
                    triedSecureRef.current = true;
                }

                const errorMessage = isSecure
                    ? 'WSS connection error - check TLS certificates and server configuration'
                    : 'WS connection error - check server availability';

                setStatus(prev => ({
                    ...prev,
                    connected: false,
                    error: errorMessage,
                }));
            };

            ws.onclose = (event) => {
                console.log('Video stream WebSocket closed:', {
                    code: event.code,
                    reason: event.reason,
                    wasClean: event.wasClean,
                });

                setStatus(prev => ({
                    ...prev,
                    connected: false,
                }));

                // Auto-reconnect logic
                if (autoReconnect && reconnectAttemptsRef.current < maxReconnectAttempts) {
                    reconnectAttemptsRef.current += 1;
                    const nextAttempt = reconnectAttemptsRef.current;
                    console.log(`Reconnecting video stream... Attempt ${nextAttempt}/${maxReconnectAttempts}`);

                    setStatus(prev => ({
                        ...prev,
                        reconnectAttempts: nextAttempt,
                    }));

                    reconnectTimeoutRef.current = setTimeout(() => {
                        startStream();
                    }, reconnectDelay * nextAttempt);
                } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
                    console.error('Max reconnection attempts reached for video stream');
                    setStatus(prev => ({
                        ...prev,
                        error: 'Max reconnection attempts reached',
                    }));
                }
            };

        } catch (error) {
            console.error('Failed to create video stream WebSocket:', error);
            setStatus(prev => ({
                ...prev,
                connected: false,
                error: error instanceof Error ? error.message : 'Unknown error',
            }));
        }
    }, [wsUrl, videoElementId, autoReconnect, maxReconnectAttempts, reconnectDelay, allowInsecureFallback, cleanup]);

    const stopStream = useCallback(() => {
        console.log('Stopping video stream');
        cleanup();
    }, [cleanup]);

    const reconnect = useCallback(() => {
        console.log('Manual reconnect triggered');
        reconnectAttemptsRef.current = 0;
        triedSecureRef.current = false;
        usingInsecureRef.current = false;
        setStatus(prev => ({ ...prev, reconnectAttempts: 0, usingInsecure: false }));
        startStream();
    }, [startStream]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanup();
        };
    }, [cleanup]);

    return {
        status,
        startStream,
        stopStream,
        reconnect,
    };
};
